// Ported from dev-scrips-pm-react src/billing/components/invoice-dismissed-orders.tsx (PROD-3312 appointment Billing tab,
// step 6). PM primitives come from the PROD-3310 billing bridge; classes are PM's, unchanged (none were arbitrary).
import { useState } from 'react';
import { ChevronDown, ChevronRight, Undo2 } from 'lucide-react';
import { BridgeButton as Button, BridgeStatusChip } from '@/billing/bridge';
import { formatMoney } from '@/shared/lib/money';
import {
  codeLabel,
  DISMISS_REASON_LABEL,
  formatOrderTime,
  groupFee,
  type OrderGroup,
} from '@/billing/lib/pending-orders';

interface DismissedOrdersProps {
  groups: OrderGroup[];
  currency: string;
  /** Draft + `canEdit`: shows Restore. */
  editable: boolean;
  busy: boolean;
  onRestore: (group: OrderGroup) => void;
}

/**
 * Collapsed "Dismissed (N)" group (canvas board 1, open). Each row carries who
 * dismissed it, when and why; Restore returns it to Pending.
 */
export function DismissedOrders({ groups, currency, editable, busy, onRestore }: DismissedOrdersProps) {
  const [open, setOpen] = useState(false);
  if (groups.length === 0) return null;
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="border-t border-border-default pt-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex flex-wrap items-center gap-2 text-left text-sm font-medium text-text-secondary"
      >
        <Chevron className="size-4" aria-hidden />
        Dismissed ({groups.length})
        <span className="text-xs font-normal text-text-muted">· not billed on this invoice, orders unchanged</span>
      </button>
      {open && (
        <ul className="mt-2 border-t border-border-subtle">
          {groups.map((g) => {
            const line = g.primary;
            const fee = groupFee(g, {});
            const who = line.dismissedBy?.name;
            const when = formatOrderTime(line.dismissedAt);
            return (
              <li
                key={g.key}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-border-subtle py-2 pr-3 pl-8 last:border-0"
              >
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-text-secondary">{line.display}</span>
                    <span className="text-xs text-text-muted tabular-nums">
                      {codeLabel(line)}
                      {fee !== null && ` · ${currency} ${formatMoney(fee)}`}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted tabular-nums">
                    {['Dismissed', who, when].filter(Boolean).join(' · ')}
                    {line.dismissReason && (
                      <BridgeStatusChip semantic="neutral" tone="soft" size="xs">
                        {DISMISS_REASON_LABEL[line.dismissReason]}
                      </BridgeStatusChip>
                    )}
                    {line.dismissNote && <span className="break-words">“{line.dismissNote}”</span>}
                  </div>
                </div>
                {editable && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-brand-primary"
                    disabled={busy}
                    onClick={() => onRestore(g)}
                  >
                    <Undo2 aria-hidden /> Restore
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
